import React, { useState, useEffect, useMemo } from 'react';
import ProjectsTable from './ProjectsTable';
import ProjectDetailsModal from './ProjectDetailsModal';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import CustomFieldFilter from './CustomFieldFilter';
import { ProjectService } from '../services/projectService';
import { Button, SearchBar, Dropdown, Popover, FilterChip, Checkbox } from './ui';
import { Filter, Settings, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

const OnHoldBoard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [filters, setFilters] = useState({
    client: '',
    modelingStatus: '',
  });
  const [customFilters, setCustomFilters] = useState([]);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(() => {
    const saved = localStorage.getItem('onhold-auto-resize');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Column visibility - default all visible except due date
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('onhold-visible-columns');
    return saved ? JSON.parse(saved) : {
      view: true, // Show the info button (subitems button won't show for on-hold projects)
      jobNumber: true,
      project: true,
      client: true,
      modelingStatus: true,
      modelingState: true,
      holdReason: true,
      daysOnHold: true,
      holdNotes: true,
      dueDate: false, // Hide due date for on-hold
      contact: true,
    };
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('onhold-column-order');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default column order for on-hold board
    return ['view', 'jobNumber', 'project', 'client', 'modelingStatus', 'modelingState', 'holdReason', 'daysOnHold', 'holdNotes', 'dueDate', 'contact'];
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [projectsData, clientsData, employeesData] = await Promise.all([
        ProjectService.getAllProjects(),
        ProjectService.getClients(),
        ProjectService.getEmployees()
      ]);

      // Calculate days on hold for each project if it's on hold
      const projectsWithDaysOnHold = projectsData.map(project => {
        let daysOnHold = 0;
        if (project.modelingState === 'Hold' && project.lastStageChangeDate) {
          const changeDate = new Date(project.lastStageChangeDate);
          const today = new Date();
          const diffTime = Math.abs(today - changeDate);
          daysOnHold = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
        return {
          ...project,
          daysOnHold,
          holdReason: project.holdReason || project.hold_reason,
          holdNotes: project.holdNotes || project.hold_notes
        };
      });

      setProjects(projectsWithDaysOnHold);
      setClients(clientsData);
      setEmployees(employeesData);
    } catch (err) {
      console.error('Error loading on-hold projects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get unique clients for filter dropdown
  const clientOptions = useMemo(() => {
    const uniqueClients = [...new Set(projects.map(p => p.clientName).filter(Boolean))];
    const sortedClients = uniqueClients.sort();

    return [
      { value: '', label: 'All Clients' },
      ...sortedClients.map(name => ({ value: name, label: name }))
    ];
  }, [projects]);

  // Get unique modeling statuses for filter dropdown
  const statusOptions = useMemo(() => {
    const uniqueStatuses = [...new Set(projects.filter(p => p.modelingState === 'Hold').map(p => p.modelingStatus).filter(Boolean))];
    const sortedStatuses = uniqueStatuses.sort();

    return [
      { value: '', label: 'All Statuses' },
      ...sortedStatuses.map(status => ({ value: status, label: status }))
    ];
  }, [projects]);

  // Helper function to get nested field value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  };

  // Helper function to apply custom filter logic
  const applyCustomFilter = (project, filter) => {
    const fieldValue = getNestedValue(project, filter.field);
    const filterValue = filter.value?.toLowerCase() || '';

    // Handle array fields
    if (Array.isArray(fieldValue)) {
      const arrayString = fieldValue.join(', ').toLowerCase();
      switch (filter.condition) {
        case 'equals':
          return arrayString === filterValue;
        case 'contains':
          return arrayString.includes(filterValue);
        case 'starts_with':
          return arrayString.startsWith(filterValue);
        case 'ends_with':
          return arrayString.endsWith(filterValue);
        case 'is_empty':
          return fieldValue.length === 0;
        case 'is_not_empty':
          return fieldValue.length > 0;
        case 'not_equals':
          return arrayString !== filterValue;
        case 'not_contains':
          return !arrayString.includes(filterValue);
        default:
          return true;
      }
    }

    // Handle string fields
    const stringValue = String(fieldValue || '').toLowerCase();
    switch (filter.condition) {
      case 'equals':
        return stringValue === filterValue;
      case 'contains':
        return stringValue.includes(filterValue);
      case 'starts_with':
        return stringValue.startsWith(filterValue);
      case 'ends_with':
        return stringValue.endsWith(filterValue);
      case 'is_empty':
        return !fieldValue;
      case 'is_not_empty':
        return !!fieldValue;
      case 'not_equals':
        return stringValue !== filterValue;
      case 'not_contains':
        return !stringValue.includes(filterValue);
      default:
        return true;
    }
  };

  // Filter projects - show only projects on hold
  const filteredProjects = useMemo(() => {
    // Start with only projects on hold
    let filtered = projects.filter(p => p.modelingState === 'Hold');

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.projectName?.toLowerCase().includes(searchLower) ||
        project.jobNumber?.toLowerCase().includes(searchLower) ||
        project.clientName?.toLowerCase().includes(searchLower) ||
        project.holdReason?.toLowerCase().includes(searchLower) ||
        project.holdNotes?.toLowerCase().includes(searchLower) ||
        project.contactName?.toLowerCase().includes(searchLower) ||
        project.modelBuilderEmployee?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply client filter
    if (filters.client) {
      filtered = filtered.filter(p => p.clientName === filters.client);
    }

    // Apply modeling status filter
    if (filters.modelingStatus) {
      filtered = filtered.filter(p => p.modelingStatus === filters.modelingStatus);
    }

    // Apply custom filters
    if (customFilters.length > 0) {
      filtered = filtered.filter(project =>
        customFilters.every(filter => applyCustomFilter(project, filter))
      );
    }

    // Sort by days on hold (ascending - shortest hold time first)
    filtered.sort((a, b) => a.daysOnHold - b.daysOnHold);

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, searchTerm, filters, customFilters]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
      localStorage.setItem('onhold-column-order', JSON.stringify(newOrder));
    }
  };

  // Define available columns
  const availableColumns = [
    { id: 'view', label: 'Actions' },
    { id: 'jobNumber', label: 'Job Number' },
    { id: 'project', label: 'Project' },
    { id: 'client', label: 'Client' },
    { id: 'modelingStatus', label: 'Modeling Status' },
    { id: 'modelingState', label: 'State' },
    { id: 'holdReason', label: 'Hold Reason' },
    { id: 'daysOnHold', label: 'Days on Hold' },
    { id: 'holdNotes', label: 'Hold Notes' },
    { id: 'dueDate', label: 'Due Date' },
    { id: 'contact', label: 'Contact' },
  ];

  // Save column order to localStorage when it changes
  React.useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('onhold-column-order', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  const handleColumnVisibilityChange = (columnId, checked) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnId]: checked
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('onhold-visible-columns', JSON.stringify(newVisibleColumns));
  };

  const handleRowClick = (project, type) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Get hidden columns for ProjectsTable
  const hiddenColumns = useMemo(() => {
    return Object.keys(visibleColumns)
      .filter(colId => !visibleColumns[colId]);
  }, [visibleColumns]);

  if (loading) {
    return (
      <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
        <LoadingSpinner message="Loading on-hold projects..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
        <ErrorMessage message={error} onRetry={loadData} onDismiss={() => setError(null)} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Breadcrumb Navigation */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-6)',
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-400)',
        }}
      >
        <span style={{ fontWeight: 'var(--font-medium)' }}>3D Models</span>
        <span>{'>'}</span>
        <span style={{ color: 'var(--gray-200)', fontWeight: 'var(--font-semibold)' }}>On Hold</span>
      </div>

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            On Hold Projects
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {filteredProjects.length} projects on hold
          </p>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(filters.client || filters.modelingStatus || customFilters.length > 0) && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--gray-400)',
            fontWeight: 'var(--font-medium)'
          }}>
            Active filters:
          </span>
          {filters.client && (
            <FilterChip
              label={`Client: ${filters.client}`}
              onRemove={() => setFilters({ ...filters, client: '' })}
            />
          )}
          {filters.modelingStatus && (
            <FilterChip
              label={`Status: ${filters.modelingStatus}`}
              onRemove={() => setFilters({ ...filters, modelingStatus: '' })}
            />
          )}
          {customFilters.map((filter, index) => {
            const fieldOptions = [
              { key: "projectName", label: "Project Name" },
              { key: "jobNumber", label: "Job Number" },
              { key: "clientName", label: "Client" },
              { key: "holdReason", label: "Hold Reason" },
              { key: "holdNotes", label: "Hold Notes" },
              { key: "contactName", label: "Contact" },
              { key: "daysOnHold", label: "Days on Hold" },
            ];
            const fieldLabel = fieldOptions.find(f => f.key === filter.field)?.label || filter.field;
            return (
              <FilterChip
                key={filter.id}
                label={`${fieldLabel} ${filter.condition} ${filter.value || ''}`}
                onRemove={() => {
                  const newFilters = customFilters.filter(f => f.id !== filter.id);
                  setCustomFilters(newFilters);
                }}
              />
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilters({ client: '', modelingStatus: '' });
              setCustomFilters([]);
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden'
      }}>
        {/* Search Bar and Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search on-hold projects..."
              size="sm"
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <Popover
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowFilters(!showFilters);
                    setShowColumnSettings(false);
                  }}
                >
                  <Filter size={16} />
                  Filters
                </Button>
              }
              position="bottom-right"
              width="400px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <h3 style={{
                  margin: 0,
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)'
                }}>
                  Filter Projects
                </h3>

                {/* Client Filter */}
                <Dropdown
                  label="Client"
                  value={filters.client}
                  options={clientOptions}
                  onChange={(value) => setFilters({ ...filters, client: value })}
                  placeholder="Select client"
                  searchable={true}
                  size="md"
                />

                {/* Modeling Status Filter */}
                <Dropdown
                  label="Modeling Status"
                  value={filters.modelingStatus}
                  options={statusOptions}
                  onChange={(value) => setFilters({ ...filters, modelingStatus: value })}
                  placeholder="Select status"
                  size="md"
                />

                {/* Divider */}
                <div style={{ borderTop: '1px solid var(--gray-700)' }} />

                {/* Advanced Filters */}
                <div>
                  <h4 style={{
                    margin: 0,
                    marginBottom: 'var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-300)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Advanced Filters
                  </h4>
                  <CustomFieldFilter
                    currentFilters={customFilters}
                    onFiltersChange={setCustomFilters}
                    fieldOptions={[
                      { key: "projectName", label: "Project Name", type: "string" },
                      { key: "jobNumber", label: "Job Number", type: "string" },
                      { key: "clientName", label: "Client", type: "string" },
                      { key: "holdReason", label: "Hold Reason", type: "string" },
                      { key: "holdNotes", label: "Hold Notes", type: "string" },
                      { key: "contactName", label: "Contact", type: "string" },
                      { key: "daysOnHold", label: "Days on Hold", type: "number" },
                    ]}
                  />
                </div>

                {/* Footer Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-3)',
                  borderTop: '1px solid var(--gray-700)'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilters({ client: '', modelingStatus: '' });
                      setCustomFilters([]);
                    }}
                  >
                    Clear all
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowFilters(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </Popover>

            <Popover
              isOpen={showColumnSettings}
              onClose={() => setShowColumnSettings(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowColumnSettings(!showColumnSettings);
                    setShowFilters(false);
                  }}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
              width="520px"
              maxWidth="90vw"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Header with count */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-200)'
                  }}>
                    Toggle Columns
                  </h3>
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--gray-400)',
                    backgroundColor: 'var(--dark-elevated)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontWeight: 'var(--font-medium)'
                  }}>
                    {Object.values(visibleColumns).filter(Boolean).length} / {Object.keys(visibleColumns).length} visible
                  </span>
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  paddingBottom: 'var(--space-3)',
                  borderBottom: '1px solid var(--gray-700)',
                  flexWrap: 'wrap'
                }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allVisible = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allVisible[key] = true;
                      });
                      setVisibleColumns(allVisible);
                      localStorage.setItem('onhold-visible-columns', JSON.stringify(allVisible));
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const allHidden = {};
                      Object.keys(visibleColumns).forEach(key => {
                        allHidden[key] = false;
                      });
                      setVisibleColumns(allHidden);
                      localStorage.setItem('onhold-visible-columns', JSON.stringify(allHidden));
                    }}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const defaultOrder = availableColumns.map(col => col.id);
                      setColumnOrder(defaultOrder);
                      localStorage.setItem('onhold-column-order', JSON.stringify(defaultOrder));
                    }}
                  >
                    Reset Order
                  </Button>
                </div>

                {/* Column Checkboxes with Drag & Drop */}
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => visibleColumns[id])
                            .map(colId => {
                              const col = availableColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => handleColumnVisibilityChange(col.id, e.target.checked)}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => !visibleColumns[id])
                              .map(colId => {
                                const col = availableColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => handleColumnVisibilityChange(col.id, e.target.checked)}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => {
                const newValue = !autoResizeEnabled;
                setAutoResizeEnabled(newValue);
                localStorage.setItem('onhold-auto-resize', JSON.stringify(newValue));
              }}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Projects Table */}
        <div style={{ overflow: 'auto' }}>
          <ProjectsTable
            projects={filteredProjects}
            activeTab="on-hold"
            tabConfig={{
              modelingStatus: 'On Hold',
              label: 'On Hold',
              columns: columnOrder.length > 0
                ? columnOrder.filter(colId => visibleColumns[colId] !== false)
                : ['view', 'jobNumber', 'project', 'client', 'modelingStatus', 'modelingState', 'holdReason', 'daysOnHold', 'holdNotes', 'contact'],
              hiddenColumns: []
            }}
            onUpdateProject={loadData}
            onRowClick={handleRowClick}
            loading={loading}
            updatingProjects={new Set()}
            clients={clients}
            employees={employees}
            filteredEmployees={employees}
            sortBy="daysOnHold"
            sortDirection="asc"
            onSort={() => {}}
            discrepanciesByProject={{}}
            autoResizeEnabled={autoResizeEnabled}
            hiddenColumns={hiddenColumns}
          />
        </div>
      </div>

      {/* Project Details Modal */}
      {modalOpen && selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onProjectUpdated={loadData}
        />
      )}
    </div>
  );
};

export default OnHoldBoard;
